import React, { useState, useEffect } from "react";
import { useMemo } from "react";
import "../../design/checkout.css";

const Checkout = ({ isOpen, onClose, subscriptions, availableCounties=[], userSubscriptions=[] }) => {
  
  const [cardInfo, setCardInfo] = useState({
    cardNumber: "",
    expMonth: "",
    expYear: "",
    cvv: "",
    zip: "",
    nameOnCard: "",
    billingAddress: "",
    billingCity: "",
    billingState: "",
  });
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const token = localStorage.getItem("access_token");

  useEffect(() => {
    if (isOpen) {
      setCardInfo({
        cardNumber: "",
        expMonth: "",
        expYear: "",
        cvv: "",
        zip: "",
        nameOnCard: "",
        billingAddress: "",
        billingCity: "",
        billingState: "",
      });
      setSelectedAddOns([]);  
    }
  }, [isOpen]);

  const subscribedAt = useMemo(() => {
    return userSubscriptions[0]?.subscribed_at ? new Date(userSubscriptions[0].subscribed_at) : null;
  }, [userSubscriptions]);

  const daysLeftInCycle = useMemo(() => {
    if (!subscribedAt) return 30;
  
    const now = new Date();
    const billingDay = subscribedAt.getDate();
  
    let nextBillingDate = new Date(now.getFullYear(), now.getMonth(), billingDay);
    if (now >= nextBillingDate) {
      nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
    }
  
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.max(Math.ceil((nextBillingDate - now) / msPerDay), 1);
  }, [subscribedAt]);
  
  const recurringTotal = useMemo(() => {
    const mappedUserSubs = userSubscriptions
      .map(sub => availableCounties.find(c => c.id === sub.county_id))
      .filter(Boolean);

    const combined = [...mappedUserSubs, ...selectedAddOns];
    const unique = Array.from(
      new Map(combined.map(item => [`${item.id}-${item.industry}`, item])).values()
    );

    return unique.reduce((sum, county) => sum + county.price, 0);
  }, [userSubscriptions, selectedAddOns, availableCounties]);

  const proratedTotal = useMemo(() => {
    const dailyFraction = daysLeftInCycle / 30;
    return selectedAddOns.reduce((sum, c) => sum + c.price * dailyFraction, 0);
  }, [selectedAddOns, daysLeftInCycle]);

  if (!isOpen) return null; 

  const handleChange = (e) => {
    setCardInfo({ ...cardInfo, [e.target.name]: e.target.value });
  };

  const handleAddCounty = (e) => {
    const countyId = parseInt(e.target.value);
    const county = availableCounties.find((c) => c.id === countyId);
    if (county && !selectedAddOns.find((c) => c.id === countyId)) {
      setSelectedAddOns([...selectedAddOns, county]);
    }
  };

  const handleRemoveCounty = (id) => {
    setSelectedAddOns(selectedAddOns.filter((c) => c.id !== id));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
  
    const authData = {
      clientKey: "3WTT9qHgFZpeUm7fk2fgLFJb2eJx466dgPY33VZ86j5h6V5Y97GzvHCs9Gd7WmU7",
      apiLoginID: "6e3uJ7FS",
    };
  
    const cardData = {
      cardNumber: cardInfo.cardNumber,
      month: cardInfo.expMonth,
      year: cardInfo.expYear,
      cardCode: cardInfo.cvv,
      zip: cardInfo.zip,
    };
  
    const secureData = {
      authData,
      cardData,
    };
  
    const addedCountyIds = selectedAddOns.map(c => c.id);
  
    window.Accept.dispatchData(secureData, async (response) => {
      if (response.messages.resultCode === "Error") {
        setErrorMsg(response.messages.message[0].text);
        setLoading(false);
        return;
      }
  
      const opaqueData = response.opaqueData;
  
      try {
        if (userSubscriptions.length === 0) {
          const res = await fetch("https://api.deliquentauction.com/subscriptions/", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              token: opaqueData.dataValue,
              name_on_card: cardInfo.nameOnCard,
              billing_address: cardInfo.billingAddress,
              billing_city: cardInfo.billingCity,
              billing_state: cardInfo.billingState,
              billing_zip: cardInfo.zip,
              county_ids: addedCountyIds,  
            }),
          });
  
          if (!res.ok) {
            const err = await res.json();
            throw new Error(err.detail || "Failed to create subscription");
          }
        } else {
            const res = await fetch(`https://api.deliquentauction.com/subscriptions/add-multiple/`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                token: opaqueData.dataValue,
                county_ids: addedCountyIds
              }),
            });
            if (!res.ok) {
              const err = await res.json();
              throw new Error(err.detail || `Failed to add counties: ${addedCountyIds}`);
            }
        }
  
        alert("Subscription updated successfully!");
        onClose();
      } catch (err) {
        setErrorMsg(err.message || "Subscription update failed.");
      } finally {
        setLoading(false);
      }
    });
  };
  
  const availableToAdd = availableCounties.filter(
    (c) =>
      !userSubscriptions.some(
        (s) => s.county_id === c.id && s.active
      ) &&
      !selectedAddOns.some((s) => s.id === c.id)
  );

  if (!isOpen) return null;

  return (
    <div className="checkout-modal-overlay">
      <div className="checkout-modal">
        <button className="close-button" onClick={onClose}>
          ✖
        </button>
        <h2>Secure Checkout</h2>

        <h3>Current Subscriptions</h3>
        {userSubscriptions.filter(sub => sub.active).length === 0 ? (
          <p>You have no current subscriptions.</p>
        ) : (
          userSubscriptions.filter(sub => sub.active).map((sub) => {
            const county = availableCounties.find((c) => c.id === sub.county_id);

            return county ? (
              <div
                key={sub.id} className="checkout-item">
                <span>
                  {county.name}, {county.state} - {county.industry} - {county.lead_count} Leads - ${county.price.toFixed(2)}
                </span>
              </div>
            ) : null;
          })
        )}

        <h3>Add Counties</h3>
        <select onChange={handleAddCounty}>
          <option value="">Select County to Add</option>
          {[...availableToAdd]
            .sort((a, b) => a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase()))
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}, {c.state} - {c.industry} ({c.lead_count} Leads) (${c.price.toFixed(2)})
              </option>
          ))}
        </select>

        {selectedAddOns.length > 0 && (
          <>
            <h3>New Selections</h3>
            {selectedAddOns.map((c) => (
              <div key={c.id} className="checkout-item">
                <span>
                  {c.name}, {c.state} - {c.industry} - {c.lead_count} Leads - ${c.price.toFixed(2)}
                </span>
                <button onClick={() => handleRemoveCounty(c.id)}>Remove</button>
              </div>
            ))}
          </>
        )}

        <div className="checkout-total">
          {selectedAddOns.length > 0 && (
            <p>
              <strong>One-time Prorated Charge:</strong> ${proratedTotal.toFixed(2)}{" "}
              <span style={{ fontSize: "0.9em", color: "#666" }}>
                ({daysLeftInCycle} days left in cycle)
              </span>
            </p>
          )}
          <p>
            <strong>Next Monthly Total:</strong> ${recurringTotal.toFixed(2)}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="checkout-form">
          <h3>Card Information</h3>
          <input name="cardNumber" placeholder="Card Number" maxLength="16" pattern="\d{13,16}" onChange={handleChange} required />
          <input name="nameOnCard" placeholder="Name on Card" onChange={handleChange} required />

          <div className="exp-cvv">
            <select name="expMonth" onChange={handleChange} required>
              <option value="">Exp Month</option>
              {Array.from({ length: 12 }, (_, i) => {
                const month = (i + 1).toString().padStart(2, "0");
                return <option key={month} value={month}>{month}</option>;
              })}
            </select>

            <select name="expYear" onChange={handleChange} required>
              <option value="">Exp Year</option>
              {Array.from({ length: 21 }, (_, i) => {
                const year = new Date().getFullYear() + i;
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>

            <input name="cvv" placeholder="CVV" maxLength="4" onChange={handleChange} required />
          </div>

          <h3>Billing Address</h3>
          <input name="billingAddress" placeholder="Billing Address" onChange={handleChange} required />
          <input name="billingCity" placeholder="City" onChange={handleChange} required />

          <div className="zip-state">
            <select name="billingState" onChange={handleChange} required>
              <option value="">State</option>
              {[
                "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
                "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
                "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
                "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
                "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
              ].map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            <input name="zip" placeholder="Zip Code" maxLength="5" onChange={handleChange} required />
          </div>

          <button type="submit" disabled={loading || selectedAddOns.length === 0}>
            {loading ? "Processing..." : "Pay and Subscribe"}
          </button>
        </form>

        <p className="disclosure">
          By submitting this payment, you agree to a recurring monthly subscription until canceled.
        </p>
        <img 
          src="https://verify.authorize.net/anetseal/images/secure90x72.gif"
          alt="Authorize.Net Seal"
        />
        {errorMsg && <p className="error-msg">{errorMsg}</p>}
      </div>
    </div>
  );
};

export default Checkout;
